export function normaliseRoomName(value = "") {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

export function formatBedroomDisplayName(room = {}) {
  const house = String(room.house || "").trim();
  const roomName = String(room.bedroomName || room.groupName || "").trim();

  if (house && roomName) {
    return `${house} ${roomName}`;
  }

  return roomName || house || "";
}

export function getRoomByName(rooms = [], roomName = "") {
  const wanted = normaliseRoomName(roomName);
  if (!wanted) return null;

  const displayMatches = rooms.filter(
    (room) => normaliseRoomName(formatBedroomDisplayName(room)) === wanted
  );

  if (displayMatches.length === 1) {
    return displayMatches[0];
  }

  if (displayMatches.length > 1) {
    return {
      __ambiguous: true,
      reason: `Room "${roomName}" matches more than one bedroom.`,
    };
  }

  const plainMatches = rooms.filter(
    (room) =>
      normaliseRoomName(room.bedroomName || room.groupName || "") === wanted
  );

  if (plainMatches.length === 1) {
    return plainMatches[0];
  }

  if (plainMatches.length > 1) {
    return {
      __ambiguous: true,
      reason: `Room "${roomName}" exists in more than one house. Please include the house name.`,
    };
  }

  return null;
}

export function validateBedroomMove({ resident, targetRoom, currentRoomId = null }) {
  if (!resident) {
    return { valid: false, code: "resident_missing", reason: "Resident not found." };
  }

  if (!targetRoom) {
    return { valid: true, code: "unassigned" };
  }

  const residents = Array.isArray(targetRoom.residents) ? targetRoom.residents : [];
  const occupantsExcludingResident = residents.filter((r) => r.id !== resident.id);
  const capacity = Number(targetRoom.capacity) || 0;
  const isSameRoom =
    currentRoomId != null && String(currentRoomId) === String(targetRoom.id);

  const roomLabel = formatBedroomDisplayName(targetRoom);

  if (!isSameRoom && capacity > 0 && occupantsExcludingResident.length >= capacity) {
    return {
      valid: false,
      code: "room_full",
      reason: `Room "${roomLabel}" is full.`,
    };
  }

  const existingType = occupantsExcludingResident.find((r) => r?.type)?.type;
  if (existingType && resident.type && existingType !== resident.type) {
    return {
      valid: false,
      code: "mixed_type",
      reason: `Cannot mix ${resident.type}s with others in "${roomLabel}".`,
    };
  }

  const existingGender = occupantsExcludingResident.find((r) => r?.gender)?.gender;
  if (existingGender && resident.gender && existingGender !== resident.gender) {
    return {
      valid: false,
      code: "mixed_gender",
      reason: `Cannot mix genders in "${roomLabel}".`,
    };
  }

  return { valid: true, code: "ok" };
}

export function validateBedroomAssignmentByName({
  resident,
  roomName,
  rooms,
  currentRoomId = null,
}) {
  const trimmed = String(roomName || "").trim();
  if (!trimmed) {
    return { valid: true, room: null, code: "unassigned" };
  }

  const room = getRoomByName(rooms, trimmed);

  if (!room) {
    return {
      valid: false,
      room: null,
      code: "room_missing",
      reason: `Room "${trimmed}" does not exist.`,
    };
  }

  if (room.__ambiguous) {
    return {
      valid: false,
      room: null,
      code: "room_ambiguous",
      reason: room.reason,
    };
  }

  const moveResult = validateBedroomMove({
    resident,
    targetRoom: room,
    currentRoomId,
  });

  return { ...moveResult, room };
}